import React, { useMemo } from 'react';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import {
  Carousel,
  Colors,
  Flex,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useScrollThresholdValue } from '../../../hooks/useScrollThresholdValue';
import { useGoodsBasic } from '../../../queries/useGoodsBasic';
import { useGoodsDetail } from '../../../queries/useGoodsDetail';
import { localizeNumber } from 'shared/src/utils';
import { isDeliveryTypeIsToday } from '../../../utils/isDeliveryTypeIsToday';
import { TodayDeliveryRemainTimer } from './TodayDeliveryRemainTimer';
import { TopGradientShadow } from './TopGradientShadow';
import { useGetDeliveryOrderDeadlineTime } from '../../../hooks/useGetDeliveryOrderDeadlineTime';
import {
  SCROLL_ANIMATION_DURATION,
  SCROLL_ANIMATION_TRIGGER_VALUE,
} from '../../../constant/scrollValue';

type NudgingItem =
  | { type: 'today'; departure: string | null }
  | { type: 'sellCount'; count: number };

export const NudgeVerticalCarousel = () => {
  const visibilityValue = useScrollThresholdValue(
    SCROLL_ANIMATION_TRIGGER_VALUE,
    SCROLL_ANIMATION_DURATION,
  );
  const { width: screenWidth } = useScreenSize();
  const { data: basic } = useGoodsBasic();
  const { data: detail } = useGoodsDetail();
  const deliveryOrderDeadlineTime = useGetDeliveryOrderDeadlineTime(basic);

  const nudgingList = useMemo(() => {
    const list: NudgingItem[] = [];

    const isDeliveryToday = isDeliveryTypeIsToday(basic);
    if (isDeliveryToday) {
      list.push({
        type: 'today',
        departure:
          deliveryOrderDeadlineTime?.format('YYYY-MM-DD HH:mm:ss') ?? '',
      });
    }

    if (detail?.goods) {
      const { score } = detail.goods;
      if (score.sell_count > 0) {
        list.push({ type: 'sellCount', count: score.sell_count });
      }
    }

    // priority로 정렬 후 최대 3개까지
    return list.slice(0, 3);
  }, [basic, deliveryOrderDeadlineTime, detail]);

  const style = useAnimatedStyle(() => {
    if (nudgingList.length === 0) {
      return {
        height: 0,
      };
    }
    return {
      flexDirection: 'column',
      height: visibilityValue.value * 36,
      borderBottomWidth: 1,
      borderColor: Colors.border_tertiary,
      overflow: 'hidden',
    };
  });

  if (nudgingList.length === 0) {
    return null;
  }

  return (
    <Animated.View style={style}>
      <TopGradientShadow />
      <Flex
        width={screenWidth}
        borderColor={'border_tertiary'}
        // height={36}
        borderBottomWidth={1}
        backgroundColor={'white'}
        overflow={'hidden'}
      >
        <Carousel
          data={nudgingList}
          autoPlay={nudgingList.length > 1}
          pagingEnabled={true}
          autoPlayInterval={2800}
          scrollAnimationDuration={300}
          renderItem={({ item }) => {
            return (
              <Flex.Center width={screenWidth} height={36}>
                {item.type === 'today' ? (
                  <TodayDeliveryRemainTimer remainTime={item.departure} />
                ) : (
                  <Typography type={'body3'} color={'content_secondary'}>
                    {localizeNumber(item.count)}명이 이 상품을 구매하는 중이에요
                  </Typography>
                )}
              </Flex.Center>
            );
          }}
          vertical={true}
          height={36}
          width={screenWidth}
        />
      </Flex>
    </Animated.View>
  );
};
